import React from 'react'
import PropTypes from 'prop-types'
import Logo from 'js/components/Logo'
import Button from 'js/components/Button/Button'
import useBreakpoints from 'js/hooks/useBreakpoints'
import AppButtons from 'js/components/AppButtons/AppButtons'
import Header from 'js/components/Header/Header'
import BalanceText from 'js/components/BalanceText'
import cn from 'classnames'
import convert from 'htmr'
import LSButton from 'js/components/LSButton'

function HeaderButtons({ buttonText, buttonLink, nativeAd }) {
  const { LG } = useBreakpoints()
  return nativeAd ? (
    <div className="text-center lg:text-left">
      <AppButtons />
    </div>
  ) : (
    <div
      className={cn('mx-auto flex md:max-w-512 lg:block lg:max-w-none', {
        'text-center': false,
      })}
    >
      <Button
        color="white"
        big
        className="mb-16 md:mx-8 lg:mb-0 lg:ml-0 lg:mr-24"
        block={!LG}
        use="a"
        href="#start"
      >
        Learn more
      </Button>
      {buttonText && (
        <Button
          className="md:mx-8 lg:mx-0"
          outline
          big
          block={!LG}
          to={buttonLink}
        >
          {buttonText}
        </Button>
      )}
    </div>
  )
}

HeaderButtons.propTypes = {
  buttonLink: PropTypes.string,
  buttonText: PropTypes.string,
  nativeAd: PropTypes.bool,
}

function DatoPageHeader({
  defaultLayout = true,
  screenshot,
  heading,
  preamble,
  // buttonText,
  // buttonLink = '/signup',
  showButtons = true,
  firstSectionBackground = 'white',
  backgroundImage,
  overlayGradient,
  showHeader,
  partnerLogo,
  ...props
}) {
  return (
    <div className={firstSectionBackground}>
      {showHeader && <Header fixedPosition />}
      <header className="relative overflow-hidden bg-LSGreenDarkest">
        <div className="relative z-10 mx-auto flex w-full max-w-1280 items-center justify-center px-32 pt-24 lg:pt-32">
          {!showHeader && <Logo inverted />}
          {partnerLogo && (
            <>
              <span className="xl:px-50 px-20 text-white type-24-demibold md:px-30 lg:px-40">
                +
              </span>
              <span className="max-w-1/2">
                <img src={partnerLogo.url} alt="" className="h-32 md:h-56" />
              </span>
            </>
          )}
        </div>
        <section
          className="relative z-10 mx-auto flex min-h-512 w-full max-w-1280 flex-col justify-center px-32 lg:flex-row"
          style={{
            top: '-45px',
          }}
        >
          <div className="flex flex-col items-center justify-center py-0 lg:py-72 xl:py-128">
            {heading && (
              <BalanceText>
                <h1 className="mb-24 text-center text-40 font-bold text-white lg:mb-0 lg:py-36 lg:text-left lg:text-64">
                  {heading}
                </h1>
              </BalanceText>
            )}
            <div className="mx-auto max-w-576 text-center font-bold leading-body text-white md:type-20 lg:max-w-768 lg:type-24 xxl:type-28">
              {convert(preamble, {
                transform: {
                  // eslint-disable-next-line react/prop-types
                  a: function Anchor({ children, href }) {
                    return (
                      <a href={href} className="text-white underline">
                        {children}
                      </a>
                    )
                  },
                },
              })}
            </div>
            {showButtons && <HeaderButtons />}
          </div>
          {defaultLayout && screenshot && (
            <div className="mb-16 flex shrink-0 justify-center lg:mx-40 lg:mb-36">
              <picture>
                <source
                  media="(max-width: 800px)"
                  srcSet={`${screenshot}&w=200`}
                />
                <source
                  media="(min-width: 800px)"
                  srcSet={`${screenshot}&w=400&fit=crop&crop=faces,edges`}
                  sizes={'100vw'}
                />
                <img
                  src={`${screenshot}&w=400&fit=crop&crop=faces,edges`}
                  alt=""
                  className="h-1 w-full"
                  style={{
                    objectFit: 'cover',
                  }}
                />
              </picture>
            </div>
          )}
        </section>
        {props.children}
        {backgroundImage && (
          <picture className="absolute bottom-0 left-0 right-0 top-0">
            <source
              media="(max-width: 800px)"
              srcSet={backgroundImage.mobileImage}
            />
            <source
              media="(min-width: 800px)"
              srcSet={backgroundImage.responsiveImage.srcSet}
              sizes={'100vw'}
            />
            <img
              src={backgroundImage.responsiveImage.src}
              alt=""
              className="w-full"
              style={{
                objectFit: 'cover',
                height: '100%',
              }}
            />
          </picture>
        )}
        {overlayGradient && (
          <div
            style={{
              zIndex: 2,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          ></div>
        )}
      </header>
      {props.headerCta && (
        <div className="mx-auto px-16 pt-12 md:px-24 md:pb-12 lg:py-20 xl:px-64 xl:py-30 xxl:px-128">
          <LSButton
            href={props.headerCta.url}
            link={true}
            size="large"
            variant="primary"
            upperCase
          >
            <span className="relative" style={{ top: 1 }}>
              {props.headerCta.buttonText}
            </span>
          </LSButton>
        </div>
      )}
    </div>
  )
}

DatoPageHeader.propTypes = {
  nativeAd: PropTypes.bool.isRequired,
  heading: PropTypes.any.isRequired,
  preamble: PropTypes.string,
  buttonText: PropTypes.any,
  buttonLink: PropTypes.string,
  children: PropTypes.any,
  defaultLayout: PropTypes.bool,
  screenshot: PropTypes.string,
  showButtons: PropTypes.bool,
  firstSectionBackground: PropTypes.string,
  backgroundImage: PropTypes.object,
  overlayGradient: PropTypes.bool,
  showHeader: PropTypes.bool,
  headerCta: PropTypes.shape({
    buttonText: PropTypes.string,
    url: PropTypes.string,
  }),
  partnerLogo: PropTypes.shape({
    url: PropTypes.string,
  }),
}

export default DatoPageHeader
